/* eslint-disable */
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../ui/button';
import { useState, useRef, useEffect, useMemo } from 'react';
import { Input } from '../ui/input';
import { modalActions } from '@/Actions';
import { authServices } from '@/Services';
import { useAuth } from '../Auth/Login/auth';
import { notificationServices } from '@/Services/Notification';
import { Loader2, X } from 'lucide-react';
import { hasValidDomain, maskEmail } from '@/Helpers';
import { Box, Modal } from '@mui/material';

const modalStyles = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '30rem',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 0 10px 0 rgba(0, 0, 0, 0.1)',
}
const EmailVerificationModal = () => {
    const { show, data } = useSelector((state) => state.modal.emailVerificationModal);
    const { userConfig, appConfig } = useAuth();
    const [loading, setIsLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const { id } = userConfig?.userDetails || {};
    const dispatch = useDispatch();
    const [otpError, setOtpError] = useState(false);
    const inputRefs = useRef([]);
    const [isEmailValid, setIsEmailValid] = useState(true);
    const timerRef = useRef(null);
    const { allowedDomains } = appConfig?.configs ?? {};
    const [state, setState] = useState({
        step: 'email',
        email: '',
        otp: ['', '', '', ''],
        timer: 57,
    });

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    const handleVerifyClick = async () => {
        if (state.email && state.email.includes('@')) {
            const isValid = hasValidDomain(state.email, allowedDomains);
            if (!isValid) {
                setIsEmailValid(false);
                return;
            }
        };
        setIsLoading(true);
        try {
            const response = await authServices.verifyEmailOtp({
                data: {
                    email: state?.email,
                    userId: id
                }
            });
            if (response?.statusCode === 200) {
                if (timerRef.current) {
                    clearInterval(timerRef.current);
                }
                setState(prev => ({ ...prev, step: 'otp', timer: 57 }));
                timerRef.current = setInterval(() => {
                    setState(prev => {
                        const newTimer = prev.timer - 1;
                        if (newTimer <= 0) {
                            clearInterval(timerRef.current);
                            return { ...prev, timer: 0 };
                        }
                        return { ...prev, timer: newTimer };
                    });
                }, 1000);
            } else {
                notificationServices.generateNotification({
                    message: response?.errorMessage || 'Something went wrong Contact Support',
                    type: 'error'
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOTP = async () => {
        setResendLoading(true);
        try {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            setState(prev => ({ ...prev, timer: 57, otp: ['', '', '', ''] }));
            setOtpError(false);
            timerRef.current = setInterval(() => {
                setState(prev => {
                    const newTimer = prev.timer - 1;
                    if (newTimer <= 0) {
                        clearInterval(timerRef.current);
                        return { ...prev, timer: 0 };
                    }
                    return { ...prev, timer: newTimer };
                });
            }, 1000);
            inputRefs.current[0]?.focus();
            const response = await authServices.verifyOtp({
                data: {
                    resendOtp: true,
                    userId: id
                }
            });
            if (response?.statusCode === 200) {
                notificationServices.generateNotification({
                    message: `OTP resent successfully to ${maskEmail(state?.email)}. Check your email`,
                    type: 'success'
                });
            } else {
                notificationServices.generateNotification({
                    message: response?.errorMessage || 'Something went wrong Contact Support',
                    type: 'error'
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            setResendLoading(false);
        }
    };

    const handleVerifyOTP = async () => {
        setIsLoading(true);
        const otpValue = state.otp.join('');
        try {
            const response = await authServices.verifyOtp({
                data: {
                    otp: otpValue,
                    userId: id,
                    email: state?.email
                }
            });
            if (response?.statusCode === 200) {
                notificationServices.generateNotification({
                    message: 'Email verified and linked successfully. You’ll now receive OTPs on this email for console login',
                    type: 'success'
                });
                handleClose();
            } else if (response?.statusCode === 401 && response?.message === 'Invalid OTP') {
                setOtpError(true);
                notificationServices.generateNotification({
                    message: 'Invalid OTP',
                    type: 'error'
                });
            }
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleBack = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        setState(prev => ({ ...prev, step: 'email', otp: ['', '', '', ''], timer: 57 }));
        setOtpError(false);
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };


    const handleClose = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
        }
        setState({
            step: 'email',
            email: '',
            otp: ['', '', '', ''],
            timer: 57,
        });
        setOtpError(false);
        dispatch(modalActions.toggleEmailVerificationModal(false));
    };

    const handleChange = (index, value) => {
        if (value && !/^\d$/.test(value)) {
            return;
        }

        const newOtp = [...state.otp];
        newOtp[index] = value;
        setState(prev => ({ ...prev, otp: newOtp }));
        setOtpError(false);

        if (value && index < 3) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (!state.otp[index] && index > 0) {
                const newOtp = [...state.otp];
                newOtp[index - 1] = '';
                setState(prev => ({ ...prev, otp: newOtp }));
                inputRefs.current[index - 1]?.focus();
            } else if (state.otp[index]) {
                // Clear current
                const newOtp = [...state.otp];
                newOtp[index] = '';
                setState(prev => ({ ...prev, otp: newOtp }));
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === 'ArrowRight' && index < 3) {
            inputRefs.current[index + 1]?.focus();
        } else if (e.key === 'Enter' && index === 3) {
            handleVerifyOTP();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '');
        const newOtp = ['', '', '', ''];

        for (let i = 0; i < Math.min(pastedData.length, 4); i++) {
            newOtp[i] = pastedData[i];
        }

        setState(prev => ({ ...prev, otp: newOtp }));
        setOtpError(false);

        const lastIndex = Math.min(pastedData.length, 3);
        inputRefs.current[lastIndex]?.focus();
    };

    const isOtpComplete = state.otp.every(digit => digit !== '');

    return (
        <Modal
            open={show}
            aria-labelledby='modal'
            aria-describedby='modal'
            onClose={handleClose}
        >
            <Box sx={modalStyles}>
                {state.step === 'email' ? (
                    <div className='p-8 w-full flex flex-col items-start gap-2 h-[295px] w-[30rem]'>
                        <div className='flex justify-between w-full'>
                            <h2 className="mb-2 text-xl font-semibold">Update Email Address</h2>
                            <X className='cursor-pointer' size={20} onClick={handleClose} />
                        </div>
                        <p className="text-gray-600 text-sm mb-6 text-left">
                            Please verify your official email ID to update or confirm your account.
                            This ensures secure OTP-based login access.
                        </p>
                        <div className="w-full">
                            <label htmlFor="email" className="block text-md font-medium mb-2 text-left text-gray-700">
                                Official Email ID
                            </label>
                            <div className="flex gap-2 w-full">
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="Enter official email ID"
                                    value={state.email}
                                    onChange={(e) => setState(prev => ({ ...prev, email: e.target.value }))}
                                    className="flex-1"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Backspace') {
                                            setIsEmailValid(true);
                                        }
                                    }}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && state.email) {
                                            handleVerifyClick();
                                        }
                                    }}
                                />
                                <Button
                                    onClick={handleVerifyClick}
                                    disabled={!state.email || !/\S+@\S+\.\S+/.test(state.email) || loading || !isEmailValid}
                                    className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? <span className='flex items-center gap-2'> <Loader2 className="w-4 h-4 animate-spin" /> Sending OTP </span> : 'Send OTP'}
                                </Button>
                            </div>
                            {!isEmailValid ? (
                                <span className='text-red-600 mt-3 text-sm'>
                                    Oops! Make sure you’re using your official email ID ending with {allowedDomains?.join(', ') || 'udyogyantra.ai, udyogyantra.com'}.
                                </span>
                            ) : null}

                        </div>
                    </div>
                ) : (
                    <div className='p-8 w-full h-[350px] w-[30rem]'>
                        <div className='flex justify-between w-full'><h2 className="mb-2 text-xl font-semibold text-center">Enter OTP</h2> <X className='cursor-pointer' size={20} onClick={handleClose} /></div>
                        <p className="text-gray-600 text-sm mb-6 text-center">
                            We have sent an OTP to{' '}
                            <span className="font-medium">
                                {state.email}
                            </span>
                        </p>

                        <div className="flex justify-center mb-6">
                            <div className="flex justify-center gap-3 py-4">
                                {state.otp.map((digit, index) => (
                                    <input
                                        key={index}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        type="text"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleChange(index, e.target.value)}
                                        onKeyDown={(e) => handleKeyDown(index, e)}
                                        onPaste={handlePaste}
                                        className={`w-14 h-14 text-center text-2xl font-semibold border-2 rounded-lg focus:outline-none transition-colors
                                        ${otpError ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-yellow-500'}`}
                                        autoFocus={index === 0}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-between items-center mb-6">
                            <span className="text-sm text-gray-600">{formatTime(state.timer)}</span>
                            <button
                                onClick={handleResendOTP}
                                className={`text-sm ${state.timer > 0 ? 'text-gray-400 cursor-not-allowed' : 'text-yellow-600 hover:text-yellow-700 cursor-pointer transition-colors'}`}
                                disabled={state.timer > 0 || resendLoading}
                            >
                                {resendLoading ? 'Resending OTP...' : 'Resend OTP'}
                            </button>
                        </div>

                        <div className="flex gap-3">
                            <Button
                                onClick={handleBack}
                                variant="secondary"
                                className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700"
                            >
                                Back
                            </Button>
                            <Button
                                onClick={handleVerifyOTP}
                                disabled={!isOtpComplete || loading}
                                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? <span className='flex items-center gap-2'> <Loader2 className="w-4 h-4 animate-spin" /> Verifying OTP</span> : 'Verify OTP'}
                            </Button>
                        </div>
                    </div>
                )}
            </Box>
        </Modal>
    );
};

export default EmailVerificationModal;